import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ProductionWorkflow = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.companyId) return;

    const q = query(collection(db, 'companies', userData.companyId, 'productionOrders'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(ordersData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData?.companyId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Beklemede': return '#94a3b8';
      case 'İşleniyor': return '#3b82f6';
      case 'Tamamlandı': return '#06C167';
      default: return '#94a3b8';
    }
  };

  const getProgressPercentage = (order) => {
    if (!order.steps || order.steps.length === 0) return 0;
    const completed = order.steps.filter(s => s.status === 'Tamamlandı').length;
    return Math.round((completed / order.steps.length) * 100);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: '"Inter", sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1a1d29', margin: 0 }}>Üretim İş Akışı</h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>Tüm üretim emirlerinin aşama bazlı canlı takibi</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '16px', border: '1px dashed #e2e8f0' }}>
             <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🏭</span>
             <p style={{ color: '#64748b', fontSize: '16px' }}>Henüz aktif bir üretim emri bulunmuyor.</p>
          </div>
        ) : (
          orders.map(order => (
            <div key={order.id} style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Order Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/uretim/emirler/${order.id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>{order.product}</span>
                    <span style={{ 
                      fontSize: '12px', 
                      background: '#f1f5f9', 
                      color: '#64748b', 
                      padding: '2px 8px', 
                      borderRadius: '6px',
                      fontWeight: '600'
                    }}>#{order.id.substring(0, 8).toUpperCase()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '15px', color: '#64748b', fontSize: '13px' }}>
                    <span><strong>Miktar:</strong> {order.quantity} {order.unit}</span>
                    <span><strong>Planlanan:</strong> {new Date(order.plannedDate).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#06C167' }}>%{getProgressPercentage(order)}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>İlerleme</div>
                </div>
              </div>

              {/* Workflow Stepper */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0', 
                padding: '20px 0',
                overflowX: 'auto',
                scrollbarWidth: 'none'
              }}>
                {order.steps?.map((step, index) => (
                  <React.Fragment key={index}>
                    {/* Step Node */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '120px',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: step.status === 'Tamamlandı' ? '#06C167' : (step.status === 'İşleniyor' ? '#3b82f6' : 'white'),
                        border: `2px solid ${step.status === 'Beklemede' ? '#e2e8f0' : (step.status === 'Tamamlandı' ? '#06C167' : '#3b82f6')}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: step.status === 'Beklemede' ? '#cbd5e1' : 'white',
                        fontWeight: '700',
                        fontSize: '14px',
                        zIndex: 2,
                        transition: 'all 0.3s ease'
                      }}>
                        {step.status === 'Tamamlandı' ? '✓' : index + 1}
                      </div>
                      <div style={{
                        marginTop: '10px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: step.status === 'Beklemede' ? '#94a3b8' : '#1e293b',
                        textAlign: 'center',
                        maxWidth: '100px'
                      }}>
                        {step.name}
                      </div>
                      {step.location && (
                        <div style={{
                          fontSize: '10px',
                          color: '#64748b',
                          marginTop: '2px'
                        }}>
                          {step.location}
                        </div>
                      )}
                    </div>

                    {/* Connector Line */}
                    {index < order.steps.length - 1 && (
                      <div style={{
                        flex: 1,
                        height: '2px',
                        background: step.status === 'Tamamlandı' ? '#06C167' : '#e2e8f0',
                        marginTop: '-36px', // Align with nodes
                        minWidth: '40px'
                      }} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Accessories Status (Summary) */}
              {order.accessories && order.accessories.length > 0 && (
                <div style={{ 
                  borderTop: '1px solid #f1f5f9', 
                  paddingTop: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b' }}>Aksesuarlar:</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {order.accessories.map((acc, aIdx) => (
                      <span key={aIdx} style={{
                        fontSize: '11px',
                        background: acc.status === 'Tedarik Edildi' ? '#dcfce7' : '#f1f5f9',
                        color: acc.status === 'Tedarik Edildi' ? '#166534' : '#64748b',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${acc.status === 'Tedarik Edildi' ? '#bbf7d0' : '#e2e8f0'}`
                      }}>
                        {acc.productName} {acc.status === 'Tedarik Edildi' ? '✓' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductionWorkflow;
